/**
 * Upload the next version (f15.1): "This will become v{n+1}". Earlier versions never change.
 * f15.3: an unticked-by-default checkbox also moves the published pointer (Q-f15.3-1).
 */

import { useState } from 'react';
import { Upload } from 'lucide-react';

import type { ArtifactDetail } from '@/api/client';
import { generalMessage, problemFor } from '@/api/fieldErrors';
import { useUploadArtifactVersion } from '@/api/queries';
import { DialogField, Modal } from '@/routes/reference-data/Modal';

import { typeLabel } from './artifactView';
import { FileDropzone } from './FileDropzone';

export function UploadVersionDialog({ artifact, onClose }: { artifact: ArtifactDetail; onClose: () => void }) {
  const upload = useUploadArtifactVersion(artifact);
  const [file, setFile] = useState<File | null>(null);
  const [releaseNote, setReleaseNote] = useState('');
  const [publish, setPublish] = useState(false);
  const next = (artifact.versions[0]?.version_number ?? 0) + 1;
  const failure = upload.error;

  return (
    <Modal
      title="Upload new version"
      subtitle={`This will become v${next}`}
      onClose={onClose}
      onSubmit={() => {
        if (file) upload.mutate({ file, releaseNote: releaseNote.trim() || undefined, publish }, { onSuccess: onClose });
      }}
      busy={upload.isPending}
      submitDisabled={!file}
      submitLabel={publish ? `Publish v${next}` : `Upload v${next}`}
      submitIcon={<Upload aria-hidden="true" />}
      layout="plain"
      message={generalMessage(failure, 'file', 'release_note')}
    >
      <div className="stack-sm">
        <div className={`field${problemFor(failure, 'file') ? ' has-problem' : ''}`}>
          <label>File</label>
          <FileDropzone
            file={file}
            onFile={setFile}
            disabled={upload.isPending}
            prompt={`Drop a ${typeLabel(artifact.content_type)} here`}
          />
          {problemFor(failure, 'file') && (
            <span className="field-error" role="alert">
              {problemFor(failure, 'file')}
            </span>
          )}
        </div>
        <DialogField label="Release note" htmlFor="version-note" problem={problemFor(failure, 'release_note')}>
          <input
            id="version-note"
            type="text"
            maxLength={500}
            value={releaseNote}
            placeholder="What changed in this version"
            onChange={(event) => setReleaseNote(event.target.value)}
          />
        </DialogField>
        <label className="check">
          <input
            type="checkbox"
            checked={publish}
            disabled={upload.isPending}
            onChange={(event) => setPublish(event.target.checked)}
          />
          <span className="ck-body">
            Make v{next} the published version
            <span className="hint">Leave unticked to upload without moving the external link.</span>
          </span>
        </label>
      </div>
    </Modal>
  );
}
